import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface GuideCreationState {
  country: string;
  city: string;
  duration: number;
  attractions: number[];
  transports: number[];
  day_period: string;
  payment_preference: string;
  physical_activity: string;
  public: boolean;
  activities: {
    name: string;
    price: number;
  }[];
  step: number;
  generating: boolean;
  error: string | null;
}

const initialState: GuideCreationState = {
  country: '',
  city: '',
  duration: 3,
  attractions: [],
  transports: [],
  day_period: '',
  payment_preference: '',
  physical_activity: '',
  public: false,
  activities: [],
  step: 1,
  generating: false,
  error: null,
};

export const guideCreationSlice = createSlice({
  name: 'guideCreation',
  initialState,
  reducers: {
    setCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
    },
    setCity: (state, action: PayloadAction<string>) => {
      state.city = action.payload;
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
    addAttraction: (state, action: PayloadAction<number>) => {
      if (!state.attractions.includes(action.payload)) {
        state.attractions.push(action.payload);
      }
    },
    removeAttraction: (state, action: PayloadAction<number>) => {
      state.attractions = state.attractions.filter(attr => attr !== action.payload);
    },
    setAttractions: (state, action: PayloadAction<number[]>) => {
      state.attractions = action.payload;
    },
    addTransportation: (state, action: PayloadAction<number>) => {
      if (!state.transports.includes(action.payload)) {
        state.transports.push(action.payload);
      }
    },
    removeTransportation: (state, action: PayloadAction<number>) => {
      state.transports = state.transports.filter(item => item !== action.payload);
    },
    setTransportation: (state, action: PayloadAction<number[]>) => {
      state.transports = action.payload;
    },
    setDayPeriod: (state, action: PayloadAction<string>) => {
      state.day_period = action.payload;
    },
    setPaymentPreference: (state, action: PayloadAction<string>) => {
      state.payment_preference = action.payload;
    },
    setPhysicalActivity: (state, action: PayloadAction<string>) => {
      state.physical_activity = action.payload;
    },
    setPublic: (state, action: PayloadAction<boolean>) => {
      state.public = action.payload;
    },
    addActivity: (state, action: PayloadAction<{ name: string; price: number }>) => {
      state.activities.push(action.payload);
    },
    removeActivity: (state, action: PayloadAction<string>) => {
      state.activities = state.activities.filter(activity => activity.name !== action.payload);
    },
    nextStep: (state) => {
      state.step += 1;
    },
    prevStep: (state) => {
      if (state.step > 1) {
        state.step -= 1;
      }
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    startGenerating: (state) => {
      state.generating = true;
      state.error = null;
    },
    generatingComplete: (state) => {
      state.generating = false;
    },
    generatingError: (state, action: PayloadAction<string>) => {
      state.generating = false;
      state.error = action.payload;
    },
    resetCreation: () => initialState,
  },
});

export const {
  setCountry,
  setCity,
  setDuration,
  addAttraction,
  removeAttraction,
  setAttractions,
  addTransportation,
  removeTransportation,
  setTransportation,
  setDayPeriod,
  setPaymentPreference,
  setPhysicalActivity,
  setPublic,
  addActivity,
  removeActivity,
  nextStep,
  prevStep,
  setStep,
  startGenerating,
  generatingComplete,
  generatingError,
  resetCreation,
} = guideCreationSlice.actions;

export default guideCreationSlice.reducer;
